import { Box, Tooltip, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import NoData from '../../../../components/NoData';
import PathCircularProgress from '../../../../components/PathCircularProgress';
import { SystemAlert } from '../../../../model/reporting/systemAlert';
import { selectSystemAlerts } from '../../../../redux/selectors/adminMonitoringSelectors';
import {
  getComparator,
  Order,
  StyledTableCell,
  StyledTableRow,
} from '../../TableComponents';

const TableDetailContainer = styled(Box)(() => ({
  height: 'fit-content',
  width: '100%',
  padding: '10px 20px 10px 20px',
}));

export interface HeadCell {
  id: keyof SystemAlert;
  label: string;
  numeric: boolean;
  toolTip?: string;
}

export interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof SystemAlert
  ) => void;
  order: Order;
  orderBy: string;
}

function SystemAlertsTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const { t } = useTranslation(['monitoring', 'common']);
  const createSortHandler =
    (property: keyof SystemAlert) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const headCells: readonly HeadCell[] = [
    {
      id: 'id',
      numeric: false,
      label: t('systemAlerts.columns.id'),
    },
    {
      id: 'firedDateTime',
      numeric: false,
      label: t('systemAlerts.columns.firedDateTime'),
    },
    {
      id: 'name',
      numeric: false,
      label: t('systemAlerts.columns.name'),
    },
    {
      id: 'severity',
      numeric: false,
      label: t('systemAlerts.columns.severity'),
    },
    {
      id: 'status',
      numeric: false,
      label: t('systemAlerts.columns.status'),
    },
    {
      id: 'type',
      numeric: false,
      label: t('systemAlerts.columns.type'),
    },
    {
      id: 'userStatus',
      numeric: false,
      label: t('systemAlerts.columns.userStatus'),
    },
    {
      id: 'insertTime',
      numeric: false,
      label: t('systemAlerts.columns.insertTime'),
    },
  ];

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align="left"
            sortDirection={orderBy === headCell.id ? order : false}
            sx={
              headCell.id === 'name'
                ? { width: '200px', maxWidth: '200px' }
                : {}
            }
          >
            <Tooltip title={headCell.toolTip || ''}>
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? t('common:table.sortedDescending')
                      : t('common:table.sortedAscending')}
                  </Box>
                ) : null}
              </TableSortLabel>
            </Tooltip>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface Props {
  isLoading: boolean;
  onSelectAlert: (alert: SystemAlert) => void;
}

export const SystemAlertsTable: React.FC<Props> = ({
  isLoading,
  onSelectAlert,
}) => {
  const { t } = useTranslation('monitoring');
  const SystemAlerts = useSelector(selectSystemAlerts);
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] =
    React.useState<keyof SystemAlert>('firedDateTime');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof SystemAlert
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <TableDetailContainer>
      <Typography
        sx={{ padding: '5px 0px 5px 0px' }}
        variant="h5"
        gutterBottom
        component="h2"
      >
        {t('systemAlerts.title')}
      </Typography>
      {!isLoading ? (
        SystemAlerts && SystemAlerts.length ? (
          <Paper sx={{ width: '100%', mb: 2 }}>
            <TableContainer sx={{ maxHeight: 1080, minWidth: 750 }}>
              <Table stickyHeader aria-labelledby="system-alerts-table">
                <SystemAlertsTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {SystemAlerts.slice()
                    .sort(getComparator(order, orderBy))
                    .map((systemAlert) => {
                      return (
                        <StyledTableRow
                          hover
                          tabIndex={-1}
                          key={systemAlert.id}
                          onClick={() => onSelectAlert(systemAlert)}
                        >
                          <StyledTableCell align="left">
                            {systemAlert.id}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {new Date(systemAlert.firedDateTime).toLocaleString(
                              'en-US',
                              {
                                weekday: 'short', // Mon
                                year: 'numeric', // 2024
                                month: 'short', // Oct
                                day: '2-digit', // 14
                                hour: '2-digit', // HH
                                minute: '2-digit', // MM
                                hour12: false, // 24-hour format
                              }
                            )}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {systemAlert.name}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {systemAlert.severity}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {systemAlert.status}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {systemAlert.type}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {systemAlert.userStatus}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {new Date(systemAlert.insertTime).toLocaleString(
                              'en-US',
                              {
                                weekday: 'short', // Mon
                                year: 'numeric', // 2024
                                month: 'short', // Oct
                                day: '2-digit', // 14
                                hour: '2-digit', // HH
                                minute: '2-digit', // MM
                                hour12: false, // 24-hour format
                              }
                            )}
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <NoData message={t('systemAlerts.noData')} />
        )
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100px', justifyContent: 'center' }}>
          <PathCircularProgress />
        </Box>
      )}
    </TableDetailContainer>
  );
};

export default SystemAlertsTable;
